import { EarningType, GetSalaryDetailsQuery } from "../../../__generated__/graphql";
import React, { useMemo } from "react";
import { useTheme } from "../../../hooks/use-theme";
import { useCurrencyFormatter, useI18n } from "../../../hooks/i18n-hooks";
import VStack from "../../../ui_atoms/VStack";
import { View } from "react-native";
import { StyleSheet } from "react-native";
import { ModalDataType } from "../MissionSalary";
import IconButton from "../../IconButton";
import Text from "../../Text";
import { BodySmallText } from "../../FormattedText";

export const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                salaryDetailsItem: {
                    alignItems: "flex-start",
                },
                salaryDetailsItemRow: {
                    position: "relative",
                    flexDirection: "row",
                },
                salaryDetailsHelpRow: {
                    alignSelf: "stretch",
                    justifyContent: "space-between",
                    display: "flex",
                    flexDirection: "row",
                },
                salaryDetailsItemRowLabel: {
                    flex: 1,
                    textAlign: "left",
                },
                salaryDetailsItemRowValue: {
                    textAlign: "right",
                    fontWeight: "700",
                    fontVariant: ["tabular-nums"],
                },
                helpButtonIcon: {
                    margin: 0,
                    position: "relative",
                    right: 2,
                    top: -10,
                },
            }),
        [],
    );
};

type SalaryBreakdownProps = {
    salaryDetails: GetSalaryDetailsQuery;
    setModalData: (modalData: ModalDataType | null) => void;
    earningType: EarningType;
};

type SalaryDetailRowProps = {
    label: string;
    helpTitle?: string;
    helpMarkdown?: string;
    salary: number;
    setModalData: (modalData: ModalDataType | null) => void;
};

const SalaryDetailRow: React.FC<SalaryDetailRowProps> = ({
    label,
    helpTitle,
    helpMarkdown,
    salary,
    setModalData,
}) => {
    const styles = useStyles();
    const { fonts } = useTheme();
    const i18n = useI18n();
    const currencyFormatter = useCurrencyFormatter();
    return (
        <View style={styles.salaryDetailsHelpRow}>
            <Text numberOfLines={2} style={[fonts.bodyMedium, styles.salaryDetailsItemRowLabel]}>
                {label}
            </Text>
            {helpTitle && helpMarkdown && (
                <IconButton
                    icon="help-circle"
                    onPress={() => {
                        const markdown = helpMarkdown;

                        setModalData({
                            titlePrefix: i18n.t("screens.user.create_mission_salary.help"),
                            title: helpTitle,
                            markdown,
                        });
                    }}
                    style={styles.helpButtonIcon}
                />
            )}
            <Text numberOfLines={1} style={[fonts.bodyMedium, styles.salaryDetailsItemRowValue]}>
                {currencyFormatter({
                    amountInCents: salary,
                })}
            </Text>
        </View>
    );
};
const SalaryBreakdown: React.FC<SalaryBreakdownProps> = ({
    salaryDetails,
    setModalData,
    earningType,
}) => {
    const { colors } = useTheme();
    const styles = useStyles();
    const i18n = useI18n();
    const prefix = React.useMemo(() => {
        if (earningType === EarningType.Monthly) {
            return "month";
        } else if (earningType === EarningType.Hourly) {
            return "hour";
        } else if (earningType === EarningType.Daily) {
            return "day";
        } else if (earningType === EarningType.Package) {
            return "package";
        } else {
            throw "Unknown earning type";
        }
    }, [earningType]);
    const isMonthSalary = earningType === EarningType.Monthly;
    return (
        <>
            <VStack style={styles.salaryDetailsItem} gap="1/2">
                <VStack style={{ width: "100%" }}>
                    <SalaryDetailRow
                        label={i18n.t(`screens.user.create_mission_salary.invoice_${prefix}_label`)}
                        setModalData={setModalData}
                        salary={salaryDetails.getSalaryDetails.invoiceSalary}
                    />
                    <BodySmallText color={colors.onSurfaceDisabled}>
                        {i18n.t("screens.user.create_mission_salary.withoutTaxes")}
                    </BodySmallText>
                </VStack>
                <VStack style={{ width: "100%" }}>
                    <SalaryDetailRow
                        label={i18n.t(`screens.user.create_mission_salary.gross_${prefix}_label`)}
                        setModalData={setModalData}
                        salary={salaryDetails.getSalaryDetails.grossSalary}
                    />
                    {isMonthSalary && salaryDetails.getSalaryDetails.mandatory13thMonth && (
                        <BodySmallText color={colors.onSurfaceDisabled}>
                            {i18n.t("screens.user.create_mission_salary.13th_salary_included")}
                        </BodySmallText>
                    )}
                </VStack>
                <SalaryDetailRow
                    label={i18n.t(`screens.user.create_mission_salary.net_${prefix}_label`)}
                    setModalData={setModalData}
                    salary={salaryDetails.getSalaryDetails.netSalary}
                />
            </VStack>
        </>
    );
};

export default SalaryBreakdown;
